import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SearchBar from '../components/SearchBar'
import Newsletter from '../components/Newsletter'
import Faq from '../components/Faq'
import SchemaMarkup from '../components/SchemaMarkup'
import supabase from '../services/supabase'
import { FaTimes } from 'react-icons/fa'
import '../styles/global.css'

const ComparisonPage = () => {
  const navigate = useNavigate()
  // State for code type selection
  const [codeType, setCodeType] = useState('cnae') // Default to CNAE

  // State for selected codes
  const [selectedCodes, setSelectedCodes] = useState([])

  // State for comparison results
  const [codeDetails, setCodeDetails] = useState([])

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // State to track if comparison has been performed
  const [comparisonPerformed, setComparisonPerformed] = useState(false)

  // State to control search bar reset
  const [resetSearch, setResetSearch] = useState(false)

  // FAQ data for the page
  const faqData = [
    {
      question: '¿Qué es el comparador de códigos?',
      answer:
        'El comparador de códigos es una herramienta que te permite comparar lado a lado hasta tres códigos del mismo tipo (CNAE 2025, CNAE 2009 o IAE). Esta comparación te ayuda a identificar similitudes y diferencias entre actividades económicas similares, facilitando la toma de decisiones sobre la clasificación más adecuada para tu negocio.',
    },
    {
      question: '¿Cómo puedo comparar códigos?',
      answer:
        'Para comparar códigos, primero selecciona el tipo de código (CNAE 2025, CNAE 2009 o IAE) que deseas comparar. Luego, utiliza las barras de búsqueda para encontrar y seleccionar hasta tres códigos que quieres comparar. Una vez seleccionados al menos dos códigos, haz clic en el botón "Comparar" para ver el análisis detallado lado a lado.',
    },
    {
      question: '¿Puedo comparar códigos de diferentes tipos?',
      answer:
        'No, esta herramienta está diseñada para comparar códigos del mismo tipo. Si deseas ver la correspondencia entre diferentes tipos de códigos (por ejemplo, entre CNAE 2025 y CNAE 2009, o entre CNAE e IAE), te recomendamos utilizar nuestra herramienta de correspondencia disponible en las páginas de detalles de cada código.',
    },
    {
      question: '¿Qué información se muestra en la comparación?',
      answer:
        'La comparación muestra todos los detalles relevantes de los códigos seleccionados lado a lado, incluyendo: código, título, descripción detallada, categoría, nivel, notas (si existen) y palabras clave. Esto te permite identificar fácilmente las similitudes y diferencias entre las actividades económicas comparadas.',
    },
    {
      question: '¿Para qué me sirve comparar códigos CNAE o IAE?',
      answer:
        'Comparar códigos te puede ayudar en diversas situaciones:\n\n- Cuando estás decidiendo entre dos actividades económicas similares para tu negocio\n- Para entender las diferencias sutiles entre actividades relacionadas\n- Para analizar la evolución de una actividad económica entre diferentes versiones (en el caso de CNAE 2009 vs CNAE 2025)\n- Para tomar decisiones informadas sobre la clasificación fiscal de tu empresa',
    },
    {
      question: '¿Puedo guardar o compartir los resultados de la comparación?',
      answer:
        'Actualmente, puedes guardar los resultados utilizando la función de impresión de tu navegador o haciendo capturas de pantalla. Estamos trabajando en implementar funcionalidades adicionales para guardar y compartir comparaciones en futuras actualizaciones.',
    },
  ]

  // Handle code type selection
  const handleCodeTypeChange = (type) => {
    setCodeType(type)
    setSelectedCodes([])
    setCodeDetails([])
    setComparisonPerformed(false)
    setError(null)
  }

  // Handle code selection from search bar
  const handleCodeSelect = (code) => {
    // Verificar si el código ya está seleccionado
    if (
      selectedCodes.some((c) => c.code === code.code && c.type === code.type)
    ) {
      setError('Este código ya ha sido seleccionado')
      return
    }

    // Limitar a máximo 3 códigos seleccionados
    if (selectedCodes.length >= 3) {
      setError('Solo puedes seleccionar hasta 3 códigos para comparar')
      return
    }

    setSelectedCodes([...selectedCodes, code])
    setComparisonPerformed(false)
    setError(null)

    // Reset search bar after selecting a code
    setResetSearch(true)
  }

  // Handle search reset completion
  const handleSearchReset = () => {
    setResetSearch(false)
  }

  // Handle removing a selected code
  const handleRemoveCode = (index) => {
    const newSelectedCodes = [...selectedCodes]
    newSelectedCodes.splice(index, 1)
    setSelectedCodes(newSelectedCodes)

    // También eliminar los detalles si ya se había realizado una comparación
    if (comparisonPerformed) {
      const newCodeDetails = [...codeDetails]
      newCodeDetails.splice(index, 1)
      setCodeDetails(newCodeDetails)

      // Si solo queda un código, no podemos mostrar comparación
      if (newSelectedCodes.length < 2) {
        setComparisonPerformed(false)
      }
    }
  }

  // Function to fetch code details based on code type and code
  const fetchCodeDetails = async (code) => {
    try {
      let details = null

      if (code.type === 'cnae') {
        // Fetch CNAE 2025 details
        const { data, error } = await supabase.rpc(
          'get_cnae_2025_details_by_code',
          {
            input_cnae_code: code.code,
          }
        )

        if (error) throw error
        if (data && data.length > 0) {
          details = data[0]
        }
      } else if (code.type === 'cnae2009') {
        // Fetch CNAE 2009 details
        const { data, error } = await supabase.rpc(
          'get_cnae_2009_details_by_code',
          {
            input_cnae_2009_code: code.code,
          }
        )

        if (error) throw error
        if (data && data.length > 0) {
          details = data[0]
        }
      } else if (code.type === 'iae') {
        // Fetch IAE details
        const { data, error } = await supabase.rpc('get_iae_details_by_code', {
          input_iae_code: code.code,
        })

        if (error) throw error
        if (data && data.length > 0) {
          details = data[0]
        }
      }

      if (!details) {
        throw new Error(
          `No se encontraron detalles para el código ${code.code}`
        )
      }

      return details
    } catch (error) {
      console.error('Error fetching code details:', error)
      setError(`Error al obtener detalles del código: ${error.message}`)
      return null
    }
  }

  // Handle comparison button click
  const handleCompare = async () => {
    if (selectedCodes.length < 2) {
      setError('Por favor, selecciona al menos dos códigos para comparar')
      return
    }

    setIsLoading(true)
    setError(null)
    setComparisonPerformed(false)
    setCodeDetails([])

    try {
      // Fetch details for all selected codes
      const details = []
      for (const code of selectedCodes) {
        const codeDetail = await fetchCodeDetails(code)
        if (codeDetail) {
          details.push(codeDetail)
        } else {
          throw new Error(
            `No se pudieron obtener los detalles para ${code.code}`
          )
        }
      }

      // Save comparison data to localStorage
      const comparisonData = {
        codes: selectedCodes,
        codeDetails: details,
        codeType: codeType,
      }

      localStorage.setItem('comparisonData', JSON.stringify(comparisonData))

      // Redirect to results page
      navigate('/resultados-comparacion/')
    } catch (error) {
      console.error('Error performing comparison:', error)
      setError(
        'Error al realizar la comparación. Por favor, inténtalo de nuevo.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  // Get search bar type based on code type
  const getSearchBarType = () => {
    switch (codeType) {
      case 'cnae':
        return 'both-cnae' // Mostrar tanto CNAE 2025 como CNAE 2009
      case 'iae':
        return 'iae'
      default:
        return 'both-cnae'
    }
  }

  // Get code type display name
  const getCodeTypeDisplayName = () => {
    switch (codeType) {
      case 'cnae':
        return 'CNAE'
      case 'iae':
        return 'IAE'
      default:
        return 'CNAE'
    }
  }

  // Get code type CSS class
  const getCodeTypeClass = () => {
    switch (codeType) {
      case 'cnae':
        return 'cnae'
      case 'cnae2009':
        return 'cnae2009'
      case 'iae':
        return 'iae'
      default:
        return 'cnae'
    }
  }

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-12 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Comparador de Códigos {getCodeTypeDisplayName()}
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Compara lado a lado hasta tres códigos {getCodeTypeDisplayName()} para
          identificar similitudes y diferencias entre actividades económicas.
          Esta herramienta te ayuda a tomar decisiones informadas sobre la
          clasificación más adecuada para tu negocio.
        </p>

        {/* Code Type Selection */}
        <div className='flex justify-center mb-0'>
          <div
            className='inline-flex rounded-md shadow-sm border border-border'
            role='group'
          >
            <button
              type='button'
              className={`px-4 py-2 text-sm font-medium ${
                codeType === 'cnae'
                  ? 'bg-cnae text-white'
                  : 'bg-surface text-text-primary hover:bg-card'
              } rounded-l-lg border border-border focus:z-10 focus:ring-2 focus:ring-primary`}
              onClick={() => handleCodeTypeChange('cnae')}
            >
              CNAE
            </button>
            <button
              type='button'
              className={`px-4 py-2 text-sm font-medium ${
                codeType === 'iae'
                  ? 'bg-iae text-white'
                  : 'bg-surface text-text-primary hover:bg-card'
              } rounded-r-lg border border-border focus:z-10 focus:ring-2 focus:ring-primary`}
              onClick={() => handleCodeTypeChange('iae')}
            >
              IAE
            </button>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <section className='py-2 md:py-4'>
        <div className='bg-surface rounded-lg p-6 max-w-2xl mx-auto'>
          <h2 className='text-xl font-bold mb-4 text-text-primary'>
            Buscar Códigos {getCodeTypeDisplayName()}
          </h2>
          <SearchBar
            type={getSearchBarType()}
            hideConvertButton={true}
            hideCorrespondenceButton={true}
            onCodeSelect={handleCodeSelect}
            resetSearch={resetSearch}
            onSearchReset={handleSearchReset}
          />

          {/* Selected Codes List */}
          {selectedCodes.length > 0 && (
            <div className='mt-4'>
              <h3 className='text-lg font-semibold mb-2 text-text-primary'>
                Códigos Seleccionados
              </h3>
              <div className='space-y-2'>
                {selectedCodes.map((code, index) => (
                  <div
                    key={`${code.type}-${code.code}`}
                    className='p-4 bg-card rounded-lg flex justify-between items-center'
                  >
                    <div>
                      <div className='flex items-center mb-2'>
                        <span
                          className={`px-2 py-1 text-xs font-bold rounded bg-${code.type === 'cnae' ? 'cnae' : code.type === 'cnae2009' ? 'cnae2009' : 'iae'} text-white mr-2`}
                        >
                          {code.type === 'cnae'
                            ? 'CNAE 2025'
                            : code.type === 'cnae2009'
                              ? 'CNAE 2009'
                              : 'IAE'}
                        </span>
                        <span className='font-bold'>{code.code}</span>
                      </div>
                      <p className='text-text-primary'>{code.title}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveCode(index)}
                      className='text-red-500 hover:text-red-700 p-1'
                      title='Eliminar código'
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Compare Button */}
        <div className='flex justify-center mt-8'>
          <button
            onClick={handleCompare}
            className={`px-6 py-3 rounded-md text-white font-medium ${
              selectedCodes.length >= 2 && selectedCodes.length <= 3
                ? `bg-${getCodeTypeClass()} hover:bg-opacity-90`
                : 'bg-surface cursor-not-allowed'
            } transition-colors duration-300 flex items-center`}
            disabled={
              selectedCodes.length < 2 || selectedCodes.length > 3 || isLoading
            }
          >
            {isLoading ? (
              <>
                <span className='mr-2'>Comparando...</span>
                <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
              </>
            ) : (
              'Comparar Códigos'
            )}
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mt-6'>
            {error}
          </div>
        )}
      </section>

      {/* Newsletter Section */}
      <div className='mt-12'>
        <Newsletter />
      </div>

      {/* FAQ Section */}
      <Faq
        title='Preguntas Frecuentes sobre el Comparador de Códigos'
        description='Resolvemos tus dudas sobre cómo utilizar el comparador de códigos y cómo puede ayudarte a tomar decisiones informadas sobre la clasificación de tu actividad económica.'
        faqData={faqData}
      />
    </div>
  )
}

export default ComparisonPage
